/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import { types } from 'mobx-state-tree';
import RefChecker from 'lib/@waca/ref-checker/src/js/RefChecker';
import { replaceArray } from 'util/ArrayOperations';
import DriverAnalysisCardAPI from 'api/DriverAnalysisCardAPI';

const COMET_VISID = 'com.ibm.vis.rave2comet';
const DECISIONTREE_VISID = 'com.ibm.vis.decisiontree';

export default types.model({
	show: types.optional(types.boolean, false),
	selections: types.array(types.frozen()),
	recommendations: types.optional(types.array(types.frozen()), []),
	visIndexToShow: types.number
}).views( (self) => ({
	/* Return the selection array */
	getSelections() {
		return self.selections;
	},
	/* Return local filters for this visualization */
	getLocalFilters(){
		let localFiltersJSON;
		if(self.getVisApi()){
			const localFiltersModel = self.getVisApi().getLocalFilters();
			localFiltersJSON = localFiltersModel && localFiltersModel.toJSON && localFiltersModel.toJSON();
		}
		return localFiltersJSON;
	},
	getRecommendations(){
		return self.recommendations;
	},
	getFooterRenderData(){
		return self.renderData;
	},
	/**Return a selection by index**/
	getSelectionByIndex(index){
		if(self.selections && index >=0 && index < self.selections.length){
			return self.selections[index];
		}
		return undefined;
	},
	getVisIndexToShow(){
		return self.visIndexToShow;
	},
	getVisApi() {
		return self.visApi;
	}
})).volatile((self) => ({
	renderData: null,
	drivers: null,
	refChecker: null,
	cardModel: null,
	_api : null,
	getAPI: () => {
		return self._api;
	},
	addSpecToRecommendation(recommendation) {
		const widgetSpec = recommendation && recommendation.widgets && recommendation.widgets[0] && recommendation.widgets[0].spec;
		if(self.drivers && widgetSpec){
			//TODO: CardModel should not have cometViewModel if it's not comet, ideally isDriverAnalysis() should not be needed
			if(widgetSpec.visId === DECISIONTREE_VISID && self.cardModel.isDriverAnalysis()){
				widgetSpec.possibleKeyDrivers = self.drivers ;
			}
		}
	},
	/**
	 * Given an recomendation, return the coverted widget spec
	 * @param {object} recommendation
	 * @example
	 * the recommendation example
	 *	{
	 *		widgets: [
	 *			{
	 *				spec: {
	 *					visId: 'com.ibm.vis.*',
	 *					data: {
	 *						dataViews: [{
	 *
	 *						}]
	 *					},
	 *					...
	 *				}
	 *			}
	 *		]
	 *	}
	 * @return {object} widget spec
	 */
	covertRecommendationToWidgetSpec(recommendation){
		const activeDataSourceId = self.cardModel.boardModel.layout.exploreVM.activeDataSourceId;
		if (!recommendation || !recommendation.widgets || !recommendation.widgets[0]) {
			return;
		}
		self.addSpecToRecommendation(recommendation);
		const clonedSpec = JSON.parse(JSON.stringify(recommendation.widgets[0].spec)); // to make the spec mutable
		const widgetSpec = {
			model: Object.assign({
				type: 'live',
				visTypeLocked: true,
				showTitle: undefined,
				titleMode: 'smartTitle'
			}, clonedSpec)
		};
		//modelRef in smart recommendation is not correct, replace it with activeDataSourceId for now
		widgetSpec.model.data.dataViews.forEach(dataView => dataView.modelRef = activeDataSourceId);
		return widgetSpec;
	},

	_getPromptsMap(savedPrompts){
		if (savedPrompts) {
			//TODO: SavedPrompts API changes in R7, as a result, Recommendations Spec could adapt the new getPromptSpecList() returned data structure
			//before that here we will have to do the conversion below
			const specArray = savedPrompts.getPromptSpecList();
			if(specArray && specArray.length){
				return specArray.reduce((map, spec)=>{
					map[spec.name] = spec;
					return map;
				}, {});
			}
		}
	},

	/**
	 * @return {Object} 
	 * @example
	 * {
	 * 	'quantity': 
	 * 		{
	 * 			columnId: '[great_outdoors_sales_DB2].[SLS_SALES_FACT].[QUANTITY]',
	 * 			name: 'quantity',
	 * 			...
	 * 		}
	 * }
	*/
	getSavedPrompts() {
		const dashboardApi = self.cardModel.boardModel.dashboardApi;
		return dashboardApi.getCanvasWhenReady()
			.then(canvas => canvas.getSelectedWidgets())
			.then(widgetAPIs => {
				if(widgetAPIs && widgetAPIs.length > 0) {
					//comet view has only 1 widget
					const id = widgetAPIs[0].getId();
					const widgetContent = dashboardApi.getCanvas().getContent(id);
					if (widgetContent) {
						const vis = widgetContent.getFeature('Visualization');
						return self._getPromptsMap(vis.getSavedPrompts());
					}
				}
				return null;
			});
	}
})).actions( self => {
	let listeners = [];
	const handlers = [];

	function cleanupListeners() {
		listeners.forEach(entry => entry.remove());
		listeners = [];
		handlers.forEach(entry => entry.remove());
	}

	function updateShow() {
		self.setShow(self.cardModel.isDriverAnalysis());
	}

	function setupShowResponse(visApi) {
		updateShow();
		/* preview needs to be updated for both change dataSlots and localFilters events
		we do not need listen to change:filters events because it can only happens in compare card
		comet does not show preview in compare card*/
		listeners.push(
			//TODO: Verify this when slots editor panel is working
			visApi.on('change:dataSlot', () => updateShow()),
			visApi.on('change:localFilters', () => updateShow())
		);
		const visLayouts = self.cardModel.getVisualizations();
		if(visLayouts && visLayouts.length) {
			const visLayout = visLayouts[0];
			const id = visLayout.widgetLayout && visLayout.widgetLayout.id;
			if(id) {
				const content = self.cardModel.boardModel.dashboardApi.getCanvas().getContent(id);
				if(content) {
					const visualization = content.getFeature('Visualization');
					visualization.on('change:type', updateShow);
					listeners.push({
						remove: () => {
							visualization.off('change:type', updateShow);
						}
					});
				}
			}
		}
	}

	return {
		afterCreate() {
			self.refChecker = new RefChecker(self);
			self._api = new DriverAnalysisCardAPI(self);
		},
		setCardModel(cardModel) {
			self.cardModel = cardModel;
		},
		setVisApi(visApi) {
			cleanupListeners();
			setupShowResponse(visApi);
			self.visApi = visApi;
		},
		setShow(show) {
			self.show = show;
		},
		setDrivers(drivers){
			self.drivers = drivers;
		},
		setSelections(selections) {
			self.selections = selections;
		},
		/**
		* For comet View model, when we set the render data for footer view,
		* we only set the first element of comet chart's render data results
		* array.
		*/
		setFooterRenderData(data, visId){
			if(visId === COMET_VISID && data && data.results && data.results.length >0){
				const newData = JSON.parse(JSON.stringify(data)); // make sure the recommendation is mutable
				self.renderData = newData;
			}
		},
		setRecommendations(recommendations) {
			self.recommendations = recommendations ;
			self.setVisIndexToShow(0);
		},
		setVisIndexToShow(index){
			self.visIndexToShow = index;
		},
		beforeDestroy() {
			self.refChecker.remove();
			cleanupListeners();
			self.visApi = null;
		},

		hookupCustomDataHandler (){
			self.setSelections([]);
			cleanupListeners();
			const widgets = self.cardModel.getVisualizations();
			widgets.forEach((widget) => {
				if (widget.widgetLayout) {
					self._hookupCustomDataHandler(widget.widgetLayout.id);
				}
			});
		},

		_hookupCustomDataHandler(widgetId) {
			const dashboardApi = self.cardModel.boardModel.dashboardApi;
			return dashboardApi.getCanvasWhenReady()
				.then(canvas => canvas.getWidgetWhenReady(widgetId))
				.then(widgetApi => {
					if(widgetApi.getVisApi){
						self.setVisApi(widgetApi.getVisApi());
					}
					const listenerRenderComplete = widgetApi.on('renderComplete', () => {
						self._loadDefaultCustomDataSelections(widgetApi);
					});
					listeners.push(listenerRenderComplete);

					//NOTE: The canvas and widget are ready so the content must be available here.
					const content = dashboardApi.getCanvas().getContent(widgetApi.getId());
					const visualization = content.getFeature('Visualization');
					let visKeyDrivers = content.getFeature('VisKeyDrivers');
					visKeyDrivers = visKeyDrivers && visKeyDrivers.isVisKeyDriversFeatureEnabled() ? visKeyDrivers : null;

					if (visKeyDrivers) {
						const handler = visKeyDrivers.registerUpdateHandler((response) => {
							self.setDrivers(visKeyDrivers.getPossibleKeyDrivers());
							self.setFooterRenderData(response, visualization.getDefinition().getId());
						});
						handlers.push(handler);
					} else {
						//TODO: this code will not be needed when the VisKeyDrivers feature flag is removed.
						const listenerPredictData = widgetApi.on('widgetFPDDataReady', event=>{
							self.setDrivers(self.visApi.getPossibleKeyDrivers());
							self.setFooterRenderData(event.data, event.visId);
						});
						listeners.push(listenerPredictData);
					}
				});
		},

		_loadDefaultCustomDataSelections(widgetApi){
			if (self.cardModel.isDriverAnalysis() && widgetApi.getVisApi){
				const selectedCustomData = widgetApi.getVisApi().getDecoratedCustomData('selected');
				const selections = selectedCustomData.map(function(item){
					return {
						key: item.id,
						type: 'customdata',
						source: {},
						values: [{ payload: item, customType: item.type }]
					};
				});
				const newSelections = [];
				replaceArray(newSelections, Array.isArray(selections) ? selections:[selections]);
				self.setSelections(
					newSelections
				);
			} else {
				self.setSelections(
					[]
				);
			}
		}
	};
});



// WEBPACK FOOTER //
// ./src/js/features/comet/CometViewModel.js